import { useState, useCallback, useRef } from 'react';
import {
  BarChart3, Printer, Download,
  CheckCircle2, FileText, AlertTriangle, Search,
} from 'lucide-react';

import { bankReconciliationApi }  from '@/api/bankReconciliation.api';
import { bankAccountApi }         from '@/api/bank.api';
import { usePermission }          from '@/hooks/usePermission';
import { Permissions }            from '@/constants/permissions';
import { Spinner }                from '@/components/ui/Spinner';
import { cn }                     from '@/utils/cn';

import type { BankReconciliationListDto } from '@/types/bankReconciliation.types';
import {
  RECONCILIATION_STATUS_LABELS,
  RECONCILIATION_STATUS_COLORS,
} from '@/types/bankReconciliation.types';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate = (d: string | null | undefined) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const fmtAmt = (n: number) =>
  n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Page ──────────────────────────────────────────────────────────────────────
export const BankReconciliationReportPage = () => {
  const { hasPermission } = usePermission();
  const canPrint  = hasPermission(Permissions.BNK_RECONCILE_PRINT);
  const canExport = hasPermission(Permissions.BNK_RECONCILE_EXPORT);

  const printRef = useRef<HTMLDivElement>(null);

  // ── Filter state ────────────────────────────────────────────────────────
  const [bankAccountId, setBankAccountId] = useState('');
  const [status,        setStatus]        = useState('');
  const [dateFrom,      setDateFrom]      = useState('');
  const [dateTo,        setDateTo]        = useState('');
  const [bankAccounts,  setBankAccounts]  = useState<{ id: string; accountNumber: string; accountName: string }[]>([]);
  const [baLoaded,      setBaLoaded]      = useState(false);

  // ── Data state ──────────────────────────────────────────────────────────
  const [items,   setItems]   = useState<BankReconciliationListDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [ran,     setRan]     = useState(false);

  // Load bank accounts once on first open
  if (!baLoaded) {
    setBaLoaded(true);
    bankAccountApi.getList({ pageSize: 200 } as any)
      .then(res => setBankAccounts(res.data?.data?.items ?? []))
      .catch(console.error);
  }

  const runReport = useCallback(async () => {
    setLoading(true);
    setRan(true);
    try {
      const filter: Record<string, unknown> = { page: 1, pageSize: 500 };
      if (bankAccountId) filter.bankAccountId = bankAccountId;
      if (status)        filter.status        = status;
      if (dateFrom)      filter.dateFrom      = dateFrom;
      if (dateTo)        filter.dateTo        = dateTo;

      const res  = await bankReconciliationApi.getList(filter as any);
      setItems(res.data?.data?.items ?? []);
    } catch (err) {
      console.error('Failed to run reconciliation report', err);
    } finally {
      setLoading(false);
    }
  }, [bankAccountId, status, dateFrom, dateTo]);

  // ── Summary stats ────────────────────────────────────────────────────────
  const totalRecords   = items.length;
  const totalBalanced  = items.filter(i => Math.abs(i.difference) < 0.005).length;
  const totalUnbalanced= totalRecords - totalBalanced;
  const totalDiff      = items.reduce((s, i) => s + i.difference, 0);
  const completedItems = items.filter(i => i.status === 'Completed');
  const draftItems     = items.filter(i => i.status === 'Draft');
  const submittedItems = items.filter(i => i.status === 'Submitted');

  // ── Export CSV ────────────────────────────────────────────────────────────
  const handleExportCsv = () => {
    if (items.length === 0) return;
    const headers = [
      'Reconciliation #', 'Bank Account', 'Currency', 'Statement Date',
      'Statement Opening', 'Statement Closing', 'Book Opening', 'Book Closing',
      'Cleared Credits', 'Cleared Debits', 'Uncleared Credits', 'Uncleared Debits',
      'Difference', 'Status', 'Completed Date',
    ];
    const rows = items.map(i => [
      i.reconciliationNumber,
      `${i.bankAccountCode} — ${i.bankAccountName}`,
      i.currencyCode,
      fmtDate(i.statementDate),
      i.statementOpeningBalance.toFixed(2),
      i.statementClosingBalance.toFixed(2),
      i.bookOpeningBalance.toFixed(2),
      i.bookClosingBalance.toFixed(2),
      i.clearedCredits.toFixed(2),
      i.clearedDebits.toFixed(2),
      i.unclearedCredits.toFixed(2),
      i.unclearedDebits.toFixed(2),
      i.difference.toFixed(2),
      i.status,
      fmtDate(i.completedDate),
    ]);
    const csv  = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `bank-reconciliation-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // ── Print ─────────────────────────────────────────────────────────────────
  const handlePrint = () => window.print();

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <BarChart3 size={20} className="text-teal-600" />
              Bank Reconciliation Report
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              Multi-period summary of all bank reconciliations
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canPrint && ran && items.length > 0 && (
              <button onClick={handlePrint}
                className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 text-sm font-medium rounded-lg transition-colors">
                <Printer size={15} />
                Print
              </button>
            )}
            {canExport && ran && items.length > 0 && (
              <button onClick={handleExportCsv}
                className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 text-sm font-medium rounded-lg transition-colors">
                <Download size={15} />
                Export CSV
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div ref={printRef} className="space-y-6 max-w-7xl mx-auto">

          {/* ── Filter panel ────────────────────────────────────────────────── */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 uppercase tracking-wider">
              Filter Criteria
            </h2>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">
                  Bank Account
                </label>
                <select value={bankAccountId} onChange={e => setBankAccountId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 outline-none">
                  <option value="">— All accounts —</option>
                  {bankAccounts.map(ba => (
                    <option key={ba.id} value={ba.id}>{ba.accountNumber} — {ba.accountName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">
                  Status
                </label>
                <select value={status} onChange={e => setStatus(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 outline-none">
                  <option value="">— All statuses —</option>
                  <option value="Draft">Draft</option>
                  <option value="Submitted">Submitted</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">
                  Statement Date From
                </label>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 outline-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1.5">
                  Statement Date To
                </label>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 outline-none" />
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={runReport} disabled={loading}
                className="flex items-center gap-2 px-5 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60">
                {loading ? <Spinner size="sm" /> : <Search size={15} />}
                Run Report
              </button>
            </div>
          </div>

          {/* ── Summary cards ───────────────────────────────────────────────── */}
          {ran && !loading && (
            <div className="grid grid-cols-4 gap-4">
              {[
                {
                  label: 'Total Reconciliations',
                  value: totalRecords,
                  icon: <FileText size={20} />,
                  color: 'text-slate-700 dark:text-slate-300',
                  bg:    'bg-slate-100 dark:bg-slate-700',
                },
                {
                  label: 'Draft / Submitted',
                  value: `${draftItems.length} / ${submittedItems.length}`,
                  icon: <FileText size={20} />,
                  color: 'text-amber-700 dark:text-amber-400',
                  bg:    'bg-amber-50 dark:bg-amber-900/20',
                },
                {
                  label: 'Completed',
                  value: completedItems.length,
                  icon: <CheckCircle2 size={20} />,
                  color: 'text-green-700 dark:text-green-400',
                  bg:    'bg-green-50 dark:bg-green-900/20',
                },
                {
                  label: 'Outstanding Difference',
                  value: totalUnbalanced > 0 ? `${totalUnbalanced} unbalanced` : '✓ All Balanced',
                  sub:   totalUnbalanced > 0 ? `Total diff: ${fmtAmt(totalDiff)}` : undefined,
                  icon: totalUnbalanced > 0 ? <AlertTriangle size={20} /> : <CheckCircle2 size={20} />,
                  color: totalUnbalanced > 0 ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400',
                  bg:    totalUnbalanced > 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-green-50 dark:bg-green-900/20',
                },
              ].map((card, i) => (
                <div key={i} className={cn('rounded-xl border border-slate-200 dark:border-slate-700 p-4', card.bg)}>
                  <div className={cn('mb-2', card.color)}>{card.icon}</div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{card.label}</p>
                  <p className={cn('text-lg font-bold', card.color)}>{card.value}</p>
                  {card.sub && <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">{card.sub}</p>}
                </div>
              ))}
            </div>
          )}

          {/* ── Report table ────────────────────────────────────────────────── */}
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Spinner size="lg" />
            </div>
          )}

          {!loading && ran && items.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <FileText size={36} className="mb-3 opacity-40" />
              <p className="text-sm font-medium">No reconciliations found</p>
              <p className="text-xs mt-1">Adjust your filter criteria and run the report again.</p>
            </div>
          )}

          {!loading && items.length > 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Reconciliation Summary — {items.length} record{items.length !== 1 ? 's' : ''}
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Reference</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Bank Account</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">CCY</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Stmt Date</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Stmt Closing</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Book Closing</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Cleared CR</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Cleared DR</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Uncleared CR</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Uncleared DR</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Difference</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Completed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {items.map(item => {
                      const balanced = Math.abs(item.difference) < 0.005;
                      return (
                        <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                          <td className="px-4 py-3 font-mono text-xs text-teal-600 dark:text-teal-400">
                            {item.reconciliationNumber}
                          </td>
                          <td className="px-4 py-3 text-slate-700 dark:text-slate-300 text-xs">
                            <div className="font-medium">{item.bankAccountCode}</div>
                            <div className="text-slate-400 truncate max-w-[160px]">{item.bankAccountName}</div>
                          </td>
                          <td className="px-4 py-3 text-slate-500 dark:text-gray-400 text-xs font-mono">{item.currencyCode}</td>
                          <td className="px-4 py-3 text-slate-600 dark:text-slate-400 text-xs whitespace-nowrap">
                            {fmtDate(item.statementDate)}
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-xs text-slate-700 dark:text-slate-300">
                            {fmtAmt(item.statementClosingBalance)}
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-xs text-slate-700 dark:text-slate-300">
                            {fmtAmt(item.bookClosingBalance)}
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-xs text-green-600">
                            {fmtAmt(item.clearedCredits)}
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-xs text-red-600">
                            {fmtAmt(item.clearedDebits)}
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-xs text-slate-500 dark:text-gray-400">
                            {fmtAmt(item.unclearedCredits)}
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-xs text-slate-500 dark:text-gray-400">
                            {fmtAmt(item.unclearedDebits)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className={cn(
                              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold font-mono',
                              balanced
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                            )}>
                              {balanced ? '✓ 0.00' : fmtAmt(item.difference)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={cn(
                              'px-2 py-0.5 rounded-full text-xs font-semibold',
                              RECONCILIATION_STATUS_COLORS[item.status as keyof typeof RECONCILIATION_STATUS_COLORS]
                                ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400'
                            )}>
                              {RECONCILIATION_STATUS_LABELS[item.status as keyof typeof RECONCILIATION_STATUS_LABELS]
                                ?? item.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500 dark:text-gray-400 whitespace-nowrap">
                            {fmtDate(item.completedDate)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>

                  {/* ── Totals footer ── */}
                  <tfoot className="border-t-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
                    <tr>
                      <td colSpan={4} className="px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300">
                        Totals ({items.length} records)
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs font-semibold text-slate-700 dark:text-slate-300">
                        {fmtAmt(items.reduce((s, i) => s + i.statementClosingBalance, 0))}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs font-semibold text-slate-700 dark:text-slate-300">
                        {fmtAmt(items.reduce((s, i) => s + i.bookClosingBalance, 0))}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs font-semibold text-green-700">
                        {fmtAmt(items.reduce((s, i) => s + i.clearedCredits, 0))}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs font-semibold text-red-700">
                        {fmtAmt(items.reduce((s, i) => s + i.clearedDebits, 0))}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs font-semibold text-slate-500 dark:text-gray-400">
                        {fmtAmt(items.reduce((s, i) => s + i.unclearedCredits, 0))}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs font-semibold text-slate-500 dark:text-gray-400">
                        {fmtAmt(items.reduce((s, i) => s + i.unclearedDebits, 0))}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs font-semibold">
                        <span className={cn(totalDiff === 0 ? 'text-green-700' : 'text-red-700')}>
                          {fmtAmt(totalDiff)}
                        </span>
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {!ran && (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <BarChart3 size={48} className="mb-3 opacity-20" />
              <p className="text-sm font-medium">Select filters and run the report</p>
              <p className="text-xs mt-1">Leave all filters blank to show all reconciliations.</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Print styles ────────────────────────────────────────────────────────── */}
      <style>{`
        @media print {
          body > *:not(#print-root) { display: none !important; }
          .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
};
